import { observer } from 'mobx-react-lite';
import type { ApplicationSdk } from '../../../../core/sdk';
import { useEffect } from 'react';
import { OrderStore } from '../';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';

const nameBodyTemplate = (product: any) => {
    return (
        <>
            <span className={'text-xs white-space-normal font-bold'}>{product.name}</span>
            <br />
            <span className={'text-xs'}>{product.description}</span>
        </>
    );
};
const imageBodyTemplate = (product: any) => {
    return product.image ? (
        <img
            className="sm:w-16rem xl:w-10rem shadow-2 block xl:block mx-auto border-round-xl"
            src={product.image}
            alt={product.name}
            style={{ maxWidth: 64, maxHeight: 64 }}
        />
    ) : (
        <i
            className="pi pi-image"
            style={{ fontSize: '2rem' }}
        ></i>
    );
};
const BodyCounterTemplate = observer(({ product, sdk }: { product: any; sdk: any }) => {
    return (
        <>
            <div className="flex gap-1 align-items-center">
                <Button
                    icon="pi pi-minus"
                    style={{
                        maxWidth: '32px',
                        maxHeight: '30px',
                    }}
                    className="p-button-outlined p-button-rounded"
                    onClick={() => sdk.currentStore.decrementProduct(product)}
                ></Button>
                <span className="flex  align-items-center justify-content-center w-1rem h-1rem bg-primary font-bold border-round m-1 text-sm">
                    {sdk.currentStore.productCount.find((x: any) => x.id === product.id)?.count ||
                        '0'}
                </span>
                <Button
                    icon="pi pi-plus"
                    style={{
                        maxWidth: '32px',
                        maxHeight: '30px',
                    }}
                    className="p-button-outlined p-button-rounded p-button-success"
                    onClick={() => sdk.currentStore.incrementProduct(product)}
                ></Button>
            </div>
        </>
    );
});

const SearchHeader = observer(
    ({
        sdk,
        products,
        categories,
        selectedCategory,
    }: {
        sdk: ApplicationSdk;
        products: any;
        categories: any;
        selectedCategory: any;
    }) => {
        const hasFilters = selectedCategory || sdk.currentStore.productQuickSearch; // Check if any filter is active

        const resetFilters = () => {
            sdk.currentStore.setSelectedCategory(null); // Reset the selected category
            sdk.currentStore.setProductQuickSearch(''); // Reset the search value
        };
        return (
            <div className="flex flex-column gap-2 w-full">
                {products && (
                    <Dropdown
                        placeholder="Filtere nach Kategorie"
                        emptyMessage="Keine Kategorien gefunden"
                        className="w-full"
                        options={products.length > 0 ? categories : []}
                        value={selectedCategory}
                        optionLabel="name"
                        style={{ width: '100%' }}
                        onChange={e => {
                            sdk.currentStore.setSelectedCategory(e.value); // Set the selected category
                        }}
                    />
                )}
                <div style={{ position: 'relative', width: '100%' }}>
                    <InputText
                        placeholder="Produkte durchsuchen"
                        value={sdk.currentStore.productQuickSearch}
                        onChange={e => sdk.currentStore.setProductQuickSearch(e.target.value)}
                        style={{ width: '100%', paddingRight: '3rem' }} // Add padding to avoid overlap with the button
                    />
                    <Button
                        icon="pi pi-search"
                        style={{
                            position: 'absolute',
                            right: '0.5rem',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            height: '2rem',
                            width: '2rem',
                            padding: 0,
                        }}
                        onClick={() => {
                            // Trigger search logic here
                            // Todo: Check if we need to do anything on search click
                        }}
                    />
                </div>
                {hasFilters && (
                    <Button
                        label="Filter löschen"
                        className="p-button-text p-button-danger"
                        style={{
                            width: '100%',
                            border: '1px solid #f44336', // Red border
                            color: '#f44336', // Red text
                        }}
                        onClick={resetFilters}
                    />
                )}
            </div>
        );
    }
);

const NewOrder = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    useEffect(() => {
        sdk.useStore(OrderStore);
    }, [sdk]);

    if (sdk?.currentStore?.orderFinished) {
        return (
            <div
                style={{
                    position: 'fixed',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: '#d4edda',
                    color: '#155724',
                    padding: '2rem',
                    borderRadius: '8px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                    textAlign: 'center',
                    zIndex: 1000,
                    width: '80%',
                    maxWidth: '500px',
                }}
            >
                <i
                    className="pi pi-check-circle"
                    style={{ fontSize: '2rem', marginBottom: '1rem', display: 'block' }}
                ></i>
                <h2 style={{ margin: 0, fontSize: '1.5rem' }}>Bestellung erfolgreich!</h2>
            </div>
        );
    }

    let products = sdk.currentStore?.allProducts || null;
    const categories = [
        { id: null, name: 'Alle Kategorien' },
        ...(sdk.currentStore?.categories || []),
    ];
    const selectedCategory = sdk.currentStore?.selectedCategory || null;

    if (
        sdk.currentStore?.isRepresentationObject &&
        !sdk.currentStore?.isRepresentationObjectValid
    ) {
        products = null;
    }

    return (
        <div className="card">
            <Dropdown
                loading={sdk.currentStore?.objectListLoading ? true : false}
                placeholder={
                    sdk.currentStore?.objectListLoading ? 'Bitte warten...' : 'Wähle ein Objekt'
                }
                emptyMessage={'Keine Objekte gefunden'}
                className={`w-full ${!sdk.currentStore?.selectedObject ? 'p-invalid' : ''}`}
                options={sdk.currentStore?.objectList || []}
                value={sdk.currentStore?.selectedObject || null}
                optionLabel={'name'}
                style={{
                    width: '100%',
                    marginBottom: sdk.currentStore?.selectedObject ? '10px' : '0px',
                }}
                onChange={e => {
                    sdk.currentStore.selectObject(e.value);
                }}
            />
            {!sdk.currentStore?.selectedObject && (
                <small
                    className="p-error"
                    style={{ display: 'block', marginBottom: '10px' }}
                >
                    Bitte ein Objekt auswählen
                </small> // Warnungstext
            )}
            {sdk.currentStore?.isRepresentationObject && (
                <div
                    className="field"
                    style={{ marginBottom: '1rem' }}
                >
                    <InputText
                        id="objectName"
                        value={sdk.currentStore.representationForObject || ''}
                        onChange={e => {
                            sdk.currentStore.setRepresentationForObject(e.target.value); // Aktualisiere den Namen des Objekts
                        }}
                        placeholder="Objektnamen eingeben"
                        style={{ width: '100%' }}
                        className={`${!sdk.currentStore?.isRepresentationObjectValid ? 'p-invalid' : ''}`}
                    />
                    {!sdk.currentStore?.isRepresentationObjectValid && (
                        <small
                            className="p-error"
                            style={{ display: 'block', marginBottom: '10px' }}
                        >
                            Bitte einen Objekt-Namen eingeben!
                        </small> // Warnungstext
                    )}
                </div>
            )}
            {products ? (
                <>
                    <DataTable
                        value={products}
                        size={'small'}
                        showHeaders={false}
                        emptyMessage={'Keine Produkte gefunden'}
                        header={
                            <SearchHeader
                                sdk={sdk}
                                categories={categories}
                                products={products}
                                selectedCategory={selectedCategory}
                            />
                        }
                    >
                        <Column
                            header="Image"
                            style={{ textAlign: 'center' }}
                            body={imageBodyTemplate}
                        ></Column>
                        <Column
                            field="name"
                            header="Name"
                            body={nameBodyTemplate}
                        ></Column>
                        <Column
                            body={(p: any) => (
                                <BodyCounterTemplate
                                    product={p}
                                    sdk={sdk}
                                />
                            )}
                        ></Column>
                    </DataTable>
                    <InputTextarea
                        value={sdk.currentStore.comment}
                        onChange={e => sdk.currentStore.setComment(e.target.value)}
                        rows={5}
                        cols={30}
                        style={{ width: '100%', marginBottom: '10px' }}
                        placeholder="Kommentar"
                    />
                </>
            ) : null}
        </div>
    );
});

export default NewOrder;
